import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Container } from '@material-ui/core';
import { MainLayout } from '../../layouts';
import {
  StyledWrapper,
  StyledHeader,
  StyledScript,
  StyledButton,
  StyledFooter
} from './CollectData.styled';
import './CollectData.css';
import { speechReportService } from '../../services';
import axios from "axios";
import AudioReactRecorder, { RecordState } from 'audio-react-recorder';
import { ObjectID } from 'bson';


const REACT_APP_DOLBY_API_KEY = process.env.REACT_APP_DOLBY_API_KEY;

const CollectData = (props) => {
  const initialState = {
    record: RecordState.STOP,
    link: '',
    complete: false,
    recordedBlob: null,
  };
  const { t } = useTranslation();
  const [state, setState] = useState(initialState);
  const [scripts, setScripts] = useState([]);
  const [script, setScript] = useState(null);
  const [isLoadingScript, setIsLoadingScript] = useState(true);
  const [retry, setRetry] = useState(0);
  const dolbyInputUrl = 'https://api.dolby.com/media/input';
  const dolbyEnhanceUrl = 'https://api.dolby.com/media/enhance';
  
  useEffect(() => {
    fetchScripts();
  });

  const fetchScripts = (e) => {
    if (scripts.length > 0) return;
    setIsLoadingScript(true);
    speechReportService.getSpeechRecordScripts()
      .then(res => {
        setRetry(0)
        setScript(res.data[0])
        setScripts(res.data.slice(1))
        setIsLoadingScript(false);
      })
      .catch(err => {
        if (retry === 5) {
          setRetry(0)
          alert('Fail to load script, retry!')
          return;
        }
        setRetry(retry + 1)
        fetchScripts();
      })
  }

  const loadScript = () => {
    if (scripts.length === 0) {
      fetchScripts()
    } else {
      setState(initialState);
      setScript(scripts[0])
      setScripts(scripts.slice(1));
    }
  };

  const onSkip = () => {
    loadScript();
  };

  const handleEnhanceAudio = async (audioFile, objectId) => {
    const input = `dlb://${objectId}`
    const output = `dlb://enhanced${objectId}`

    const serverUrl = await axios({
      method: 'POST',
      url: dolbyInputUrl,
      data: { url: input },
      headers: { 'x-api-key': REACT_APP_DOLBY_API_KEY }
    })
      .then(res => { return res.data.url })
      .catch(err => console.error(err))

    await axios({
      method: 'PUT',
      url: serverUrl,
      data: audioFile
    })
      .catch(err => console.error(err))

    await axios({
      method: 'POST',
      url: dolbyEnhanceUrl,
      data: { input: input, output: output },
      headers: { 'x-api-key': REACT_APP_DOLBY_API_KEY }
    })
  }

  const onSubmit = (e) => {
    if (!state.recordedBlob) return alert('Fail to send! No record file');
    
    let audioFile = new File([state.recordedBlob], script._id.toString() + ".wav");
    let id = new ObjectID();
    handleEnhanceAudio(audioFile, id);

    let formData = new FormData();
    formData.append('script_id', script._id);
    formData.append('speech_record_id', id);
    formData.append('record', state.recordedBlob, 'record');

    speechReportService.postSpeechRecord(formData)
    loadScript();
  };
  const onStop = (recordedBlob) => {
    setState(prevState => ({
      ...prevState,
      link: recordedBlob.url,
      recordedBlob: recordedBlob.blob,
    }));
  };
  const onRecord = () => {
    if (state.record === RecordState.STOP) {
      setState(prevState => ({
        ...prevState,
        record: RecordState.START,
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        record: RecordState.STOP,
        complete: true,
      }));
    }
  }
  const onRecorderClicked = () => {
    const recorder = document.getElementById('recorder');
    if (recorder.classList.contains('recording')) {
      onRecord();
      recorder.classList.remove('recording');
      recorder.classList.add('download');
      setTimeout(() => {
        recorder.classList.remove('download');
        recorder.classList.add('out');
      }, 1000);
    }
    else if (
      !recorder.classList.contains('recording') &&
      !recorder.classList.contains('download')
    ) {
      onRecord();
      recorder.classList.remove('out');
      recorder.classList.add('recording');
    }
  }

  return (
    <MainLayout>
      <StyledWrapper>
        <StyledHeader>{t('CollectDataHeader')}</StyledHeader>
        <Container id='collect-data-note' maxWidth="lg">
          <h4>{t('CollectDataInstruction')}:</h4>
          <p>{t('CollectDataRecord')}.</p>
          <p>{t('CollectDataScript')}.</p>
          <p>{t('CollectDataSend')}.</p>
          <p>{t('CollectDataSkip')}.</p>
        </Container>
        <Container maxWidth="lg" style={{ marginTop: '2em' }}>
          <Grid container direction="column" alignItems="center">
            <StyledScript>
              {!isLoadingScript ? script.script : 'Loading...'}
            </StyledScript>
            <div id="recorder" onClick={onRecorderClicked}>
              <img
                id="record"
                src="https://bassets.github.io/record.svg"
                alt="voice report"
              />
              <img
                id="arrow"
                src="https://bassets.github.io/arrow.svg"
                alt="replay"
              />
            </div>
            <AudioReactRecorder 
              state={state.record} 
              onStop={onStop} 
              canvasWidth="0px" 
              canvasHeight="0px"
            />
            {state.complete &&
              <Grid item>
                <audio className="play-back" controls src={state.link}></audio>
              </Grid>
            }
            <Grid item>
              <StyledButton
                variant="contained"
                color="primary"
                onClick={onSubmit}
                disabled={!state.recordedBlob}
              >
                <strong>{t('Send')}</strong>
              </StyledButton>
              <StyledButton
                variant="outlined"
                color="secondary"
                onClick={onSkip}
                disabled={isLoadingScript}
              >
                {t('Skip')}
              </StyledButton>
            </Grid>
          </Grid>
        </Container>
        <StyledFooter>
          <p>{t('CollectDataAcknowledgement')}</p>
          <p>{t('CollectDataCommitment')}</p>
        </StyledFooter>
      </StyledWrapper>
    </MainLayout>
  );
};

export default CollectData;
